import React, { useEffect, useRef } from "react";
import { loadLiquidGlass } from "../utils/loadLiquidGlass";
import type { LiquidGlassInstance } from "../types/liquid-glass";

export interface LiquidGlassContainerProps {
  children: React.ReactNode;
  borderRadius?: number;
  tintOpacity?: number;
  className?: string;
}

/**
 * React wrapper around liquid-glass-js Container (type: "rounded").
 * Content renders immediately; glass canvas mounts behind it when ready.
 * @see https://github.com/dashersw/liquid-glass-js
 */
const LiquidGlassContainer: React.FC<LiquidGlassContainerProps> = ({
  children,
  borderRadius = 8,
  tintOpacity = 0.22,
  className = "",
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<LiquidGlassInstance | null>(null);

  useEffect(() => {
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    const syncGlassSize = (container: LiquidGlassInstance) => {
      if (!hostRef.current) return;
      const { width, height } = hostRef.current.getBoundingClientRect();
      const w = Math.max(Math.ceil(width), 1);
      const h = Math.max(Math.ceil(height), 1);
      container.element.style.width = `${w}px`;
      container.element.style.height = `${h}px`;
      container.updateSizeFromDOM();
      container.render?.();
    };

    const setup = async () => {
      try {
        await loadLiquidGlass();
        if (cancelled || !hostRef.current) return;

        const container = new window.Container({
          borderRadius,
          type: "rounded",
          tintOpacity,
        });

        const glassEl = container.element;
        glassEl.classList.add("job-glass-container", "job-glass-layer");
        if (className) {
          glassEl.classList.add(
            ...className.split(/\s+/).filter(Boolean),
          );
        }

        hostRef.current.insertBefore(glassEl, hostRef.current.firstChild);
        instanceRef.current = container;
        hostRef.current.classList.remove("job-glass-fallback");

        resizeObserver = new ResizeObserver(() => syncGlassSize(container));
        resizeObserver.observe(hostRef.current);

        requestAnimationFrame(() => syncGlassSize(container));
      } catch (err) {
        console.error("Liquid glass init failed:", err);
        hostRef.current?.classList.add("job-glass-fallback");
      }
    };

    setup();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      const instance = instanceRef.current;
      if (instance) {
        const idx = window.Container?.instances.indexOf(instance);
        if (idx !== undefined && idx > -1) {
          window.Container.instances.splice(idx, 1);
        }
        instance.element.remove();
      }
      instanceRef.current = null;
    };
  }, [borderRadius, tintOpacity, className]);

  return (
    <div
      ref={hostRef}
      className={`job-glass-host w-full ${className}`.trim()}
      style={{ borderRadius: `${borderRadius}px` }}
    >
      <div className="job-glass-content">{children}</div>
    </div>
  );
};

export default LiquidGlassContainer;
